import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PricingSection from "@/components/PricingSection";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Zap, LineChart, FileText, Calendar, Users, Brain, ShieldCheck } from "lucide-react";

const STEPS = [
  { icon: Users, title: "Paste your website", body: "One field. We fetch it and read your headlines, offers and CTAs to understand your business." },
  { icon: Brain, title: "Get your next moves", body: "A prioritized list of what to do today — plus the assets (ad copy, keywords, landing copy) to do it with." },
  { icon: ShieldCheck, title: "Execute and check off", body: "Work through Today's Actions and the weekly checklist. Track a Growth Score that moves as you ship." },
];

const DELIVERABLES = [
  { icon: Zap, title: "Today's Actions — what to do next", tag: "Execution" },
  { icon: LineChart, title: "Growth Score + subscores", tag: "Insight" },
  { icon: FileText, title: "Google Ads campaign structure + copy", tag: "Assets" },
  { icon: Zap, title: "Meta campaign ideas & creative angles", tag: "Assets" },
  { icon: FileText, title: "Landing-page copy + FAQ + form fields", tag: "Assets" },
  { icon: Target, title: "Keyword strategy + negative keywords", tag: "Assets" },
  { icon: Calendar, title: "30-day content calendar", tag: "Assets" },
  { icon: LineChart, title: "3 planning scenarios + weekly checklist", tag: "Ops" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="chip" data-testid="hero-chip">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Built with 14+ years of digital marketing experience
              </span>
              <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tight text-zinc-900 mt-6 leading-[1.02]">
                What to do <span className="text-indigo-600">next</span><br />
                to grow your business.
              </h1>
              <p className="text-lg text-zinc-600 mt-6 max-w-xl">
                Paste your website. LeadGen reads it, infers your audience, USP and competitors, and delivers your next moves — plus the ad copy, keywords, landing copy and calendar to ship them this week.
              </p>
              <p className="text-xs text-zinc-500 mt-3 max-w-xl">
                Every forecast is an illustrative planning scenario built on industry benchmark ranges (CTR, CVR, CPC) — not a guarantee.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-8">
                <Link
                  to="/generate"
                  data-testid="hero-cta-primary"
                  className="btn-lift inline-flex items-center gap-2 h-12 px-6 rounded-full bg-indigo-600 text-white font-medium hover:bg-indigo-700"
                >
                  Get my next moves <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#pricing"
                  data-testid="hero-cta-secondary"
                  className="btn-lift inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border border-zinc-200 text-zinc-900 hover:border-zinc-400"
                >
                  See pricing
                </a>
              </div>

              <div className="flex items-center gap-6 mt-10 text-sm text-zinc-500">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((n) => (
                    <div key={n} className="h-8 w-8 rounded-full border-2 border-white"
                         style={{ background: ["#4f46e5","#10b981","#f59e0b","#ef4444"][n-1] }} />
                  ))}
                </div>
                <div>Trusted by early founders across clinics, salons, boutiques & local services.</div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-3xl overflow-hidden border border-zinc-200 shadow-2xl shadow-zinc-200/50">
                <img
                  src="https://images.unsplash.com/photo-1758598497635-48cbbb1f6555?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxjb25maWRlbnQlMjBzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXJ8ZW58MHx8fHwxNzg4MTYyMzkzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Small business owner"
                  className="w-full h-[520px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-zinc-200 shadow-lg p-4 w-64 fade-up">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Today's next move</div>
                <div className="font-display font-black text-lg text-zinc-900 mt-1 leading-tight">Set up conversion tracking · 45 min</div>
                <div className="mt-3 h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 w-2/5 progress-line" />
                </div>
                <div className="text-xs text-zinc-500 mt-2">2 of 7 actions done today</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="chip"><span className="h-1.5 w-1.5 rounded-full bg-indigo-600" /> How it works</span>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-zinc-900 mt-4">
              Next moves, not another PDF.
            </h2>
            <p className="text-zinc-600 mt-3">
              LeadGen is your marketing operating system: a Growth Score you can move, a Today's Actions list to work through, and the assets to ship each move — not a course, not theory.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {STEPS.map((s, i) => (
              <div key={`step-${s.title}`} data-testid={`step-${i}`} className="card-hover bg-zinc-50 border border-zinc-200 rounded-2xl p-8">
                <div className="h-10 w-10 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-6">Step {i + 1}</div>
                <h3 className="font-display font-black text-2xl text-zinc-900 mt-1">{s.title}</h3>
                <p className="text-zinc-600 mt-2 text-sm">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="chip"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> What you get</span>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-zinc-900 mt-4">
              A next-move list. And the assets to ship it.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
            {DELIVERABLES.map((d, i) => (
              <div key={`deliv-${d.title}`} data-testid={`deliverable-${i}`} className="card-hover bg-white border border-zinc-200 rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <d.icon className="h-5 w-5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">{d.tag}</span>
                </div>
                <div className="mt-4 font-medium text-zinc-900">{d.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection />

      {/* FINAL CTA */}
      <section className="py-24 bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.05]">
            Stop wondering what to do.<br />
            <span className="text-indigo-400">Start shipping the next move.</span>
          </h2>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto">
            Give us 3 minutes. Walk away with today's actions, a Growth Score to raise, and the assets to execute.
          </p>
          <Link
            to="/generate"
            data-testid="final-cta-btn"
            className="btn-lift inline-flex items-center gap-2 mt-10 h-12 px-6 rounded-full bg-white text-zinc-900 font-medium hover:bg-indigo-500 hover:text-white"
          >
            Get my next moves <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
